import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { env } from '../environment';
import { RootState } from '../store';
import { PaymentMethod, QueryParams } from '../interfaces';
import { objectToString } from '../helpers';

export const PaymentMethodAPI = createApi({
  reducerPath: 'paymentMethodAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: env.apiUrl + '/payment-method',
    prepareHeaders: (headers, { getState }) => {
      const { accessToken } = (getState() as RootState).auth;
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      return headers;
    }
  }),
  tagTypes: ['PaymentMethod'],
  endpoints: (build) => ({
    fetchPaymentMethods: build.query<PaymentMethod[], QueryParams>({
      query: (params) => '/?' + objectToString(params),
      providesTags: result => result
        ? [...result.map(({ id }) => ({ type: 'PaymentMethod' as const, id }))]
        : ['PaymentMethod'],
    }),
    fetchPaymentMethod: build.query<PaymentMethod, string>({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) =>
        result ? [{ type: 'PaymentMethod' as const, id }] : ['PaymentMethod'],
    }),
    createPaymentMethod: build.mutation<PaymentMethod, Partial<PaymentMethod>>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PaymentMethod'],
    }),
    updatePaymentMethod: build
      .mutation<PaymentMethod, { id: string; body: Partial<PaymentMethod> }>({
        query: ({ id, body }) => ({
          url: `/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (result, error, { id }) =>
          result ? [{ type: 'PaymentMethod' as const, id }] : ['PaymentMethod'],
      }),
    deletePaymentMethod: build.mutation<PaymentMethod, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) =>
        result ? [{ type: 'PaymentMethod' as const, id }] : ['PaymentMethod'],
    }),
  }),
});

export const {
  useFetchPaymentMethodsQuery,
  useFetchPaymentMethodQuery,
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
} = PaymentMethodAPI;